import { useFormContext } from 'react-hook-form';

import palette from '@lib/core/src/theme/palette';
import { Stack, styled, Typography, Button as MuiButton } from '@mui/material';
import {
  RiLightbulbFlashLine,
  RiFocus3Line,
  RiLink,
  RiChatAiLine,
  RiArrowRightLine,
  RiErrorWarningLine
} from '@remixicon/react';

import {
  WorkflowMeetingsContextDtoScope,
  WorkflowMeetingsContextDtoTimeRange,
  WorkflowTriggerEventDto
} from '@/api/generated';
import { Card, LargeTitle } from '@/components/AiWorkflow/AiWorkflowStyles';
import { triggerDisplay } from '@/components/AiWorkflow/formSchema';
import { useWorkflow } from '@/components/AiWorkflow/hooks/useWorkflow';
import { useChatActions } from '@/store/chat/chatStore';
import { useSelectedGoogleDriveFiles } from '@/store/googleDriveStore';
import { useSelectedMeetingsActions } from '@/store/selectedMeetingsStore';
import Markdown from '@/utils/Markdown';
import { capitalizeFirstLetterWithComma, joinArrayWithComma } from '@/utils/stringUtil';
import { launchErrorToast, launchSuccessToast } from '@/utils/toast';

const Container = styled(Card)({
  gap: 20
});

const Row = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 12
});

const IconCircle = styled('div')<{ bgcolor: string }>(({ bgcolor }) => ({
  width: 24,
  minWidth: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgcolor,
  marginRight: 0,
  position: 'relative',
  border: `1px solid ${palette.grey[1200]}`,
  marginTop: '0.5rem'
}));

const ItemText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'component'
})({
  fontWeight: 500,
  fontSize: 12,
  color: palette.black,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  maxWidth: '88%',
  whiteSpace: 'normal'
});

const ITEM_GAP = 24;

const ItemsStack = styled(Stack)({
  gap: ITEM_GAP,
  position: 'relative'
});

const Line = styled('div')({
  width: 1,
  height: `calc(100% - ${ITEM_GAP}px)`,
  backgroundColor: palette.grey[1200],
  position: 'absolute',
  top: `${ITEM_GAP / 2}px`,
  left: '12px'
});

const RunTestButton = styled(MuiButton)({
  width: '100%',
  borderRadius: 24,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: 13,
  alignSelf: 'flex-end',
  padding: '8px 12px',
  minWidth: 0,
  borderColor: palette.grey[1200],
  color: palette.black,
  backgroundColor: palette.white,
  '&:hover': {
    backgroundColor: palette.grey[100],
    borderColor: palette.grey[1200]
  }
});

const items = [
  {
    icon: <RiLightbulbFlashLine size={14} color={palette.pink[100]} />,
    bgcolor: palette.backgrounds[100],
    field: 'trigger',
    label: 'Every Day'
  },
  {
    icon: <RiFocus3Line size={14} color={palette.orange[100]} />,
    bgcolor: palette.backgrounds[200],
    field: 'prompt',
    label: 'Task prompt'
  },
  {
    icon: <RiLink size={14} color={palette.brown[100]} />,
    bgcolor: palette.backgrounds[300],
    field: 'scope',
    label: 'Based on all meetings occurred yesterday'
  },
  {
    icon: <RiChatAiLine size={14} color={palette.purple[500]} />,
    bgcolor: palette.backgrounds[400],
    field: 'notifications',
    label: 'Send output to my gmail'
  }
];

const Preview = () => {
  const selectedGoogleDriveFiles = useSelectedGoogleDriveFiles();
  const { watch, formState, getValues } = useFormContext();
  const { testWorkflow, isTestLoading } = useWorkflow();
  const { setIsChatOpen, setHasChatStarted, setActiveThreadId, setIsThreadNew, setChatTitle } =
    useChatActions();
  const { clearSelectedMeetings } = useSelectedMeetingsActions();

  const onRunTest = () => {
    clearSelectedMeetings();
    const values = getValues();

    const triggerEvent: WorkflowTriggerEventDto = {
      title: values.title,
      flow: {
        prompt: values.prompt
      },
      context: {
        meetings: {
          timeRange: values.timeRange,
          scope: values.scope,
          keywords: values.keyWords
        },
        googleDrive: {
          files: selectedGoogleDriveFiles.map(file => ({
            fileId: file.fileId ?? '',
            fileName: file.fileName ?? ''
          }))
        }
      },
      notifications: {
        email: { type: 'email' }
      }
    };

    testWorkflow(
      { data: triggerEvent },
      {
        onSuccess: (data: any) => {
          if (!data?.items?.[0]?.thread?.id) {
            launchSuccessToast('No meetings found');
            return;
          }
          setIsChatOpen(true);
          setHasChatStarted(true);
          setIsThreadNew(false);
          setChatTitle(data?.items?.[0]?.thread.title || '');
          setActiveThreadId(data?.items?.[0]?.thread.id || '');
        },
        onError: () => {
          launchErrorToast('Test Failed');
        }
      }
    );
  };

  const formatField = (
    field: string | WorkflowMeetingsContextDtoScope | WorkflowMeetingsContextDtoTimeRange
  ) => {
    const value = watch(field);
    if (field === 'notifications') {
      return value?.email ? 'Send output to my email' : 'Send output to Jamie';
    }
    if (field === 'trigger') {
      return triggerDisplay(value);
    }
    if (Array.isArray(value)) {
      return joinArrayWithComma(value);
    }
    if (field === 'scope') {
      return capitalizeFirstLetterWithComma(value?.toString() + ' meetings' || '');
    }
    return capitalizeFirstLetterWithComma(value?.toString() || '');
  };

  return (
    <Container>
      <LargeTitle>Agent Preview</LargeTitle>
      <ItemsStack>
        <Line />
        {items.map(({ icon, bgcolor, field, label }, idx) => (
          <Row key={field}>
            <IconCircle bgcolor={bgcolor}>{icon}</IconCircle>
            <ItemText as="div">
              <Markdown>{formatField(field) || label}</Markdown>
            </ItemText>
          </Row>
        ))}
      </ItemsStack>
      <RunTestButton
        onClick={onRunTest}
        disabled={!formState.isValid}
        variant="outlined"
        startIcon={<RiArrowRightLine size={16} />}
        loading={isTestLoading}
      >
        Run test
      </RunTestButton>
      <Stack flexDirection="row" alignItems="flex-start" gap={1}>
        <RiErrorWarningLine size={16} color={palette.grey[400]} />
        <Typography fontSize={12} color={palette.grey[400]}>
          The test may take up to 60 seconds to complete
        </Typography>
      </Stack>
    </Container>
  );
};

export default Preview;
